/**
 * Fire-and-forget Klaviyo onsite page-visit beacon.
 *
 * EN-958: `identify()` and `track()` both return promises - the real klaviyo.js and the pre-load
 * Proxy shim in welcome.blade.php alike - and discarding them made every ad-blocked, offline or
 * ITP-killed request an unhandled rejection. Those were roughly 350 Sentry events per 30 days,
 * stackless and so unattributable to any file. Failures are swallowed here the same way
 * api/nudge.js and api/subscription.js swallow theirs: marketing telemetry must never surface as
 * an error or interrupt navigation.
 *
 * try/catch rather than a trailing `.catch()`, because the call sits in a navigation hook and
 * klaviyo.js can also throw *synchronously* - which happens outside any promise chain and would
 * escape the hook. The `typeof` guard covers the same case the old `if (!window.klaviyo)` check
 * missed: with no Proxy support the shim falls back to a bare array carrying only `.push`.
 *
 * Awaiting in sequence rather than using identify()'s callback keeps both promises handled in one
 * place. The shim resolves its promise from the same queue callback it would otherwise invoke, so
 * ordering is unchanged; and stopping after a failed identify is correct, because without a profile
 * a track would be anonymous and would fail for the same network reason anyway.
 *
 * @param {string|undefined} email
 * @param {string|undefined} page Legacy route name, which Klaviyo flows and segments filter on.
 * @param {string|undefined} path The page's URL path.
 * @returns {Promise<void>} Always resolves. Returned for tests; app callers ignore it.
 */
const sendKlaviyoEvent = async (email, page, path) => {
	if (typeof window.klaviyo?.identify !== 'function' || !email) {
		return;
	}

	try {
		await window.klaviyo.identify({ email });
		await window.klaviyo.track('Visited page', { page, path });
	} catch {
		// Klaviyo is unreachable (content blocker, offline, ITP). Nothing to do and nothing to report.
	}
};

const exportObj = {
	sendKlaviyoEvent
};

export default exportObj;
